import type { ReactNode } from 'react';
import { Box, Center, Group, Stack, Text, Title } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { IconFigmaAlertTriangle } from '@/assets/icons';

interface ErrorCardProps {
  badge: string;
  title: string;
  message: string;
  errorCode?: string;
  actions: ReactNode;
}

export const ErrorCard = ({ badge, title, message, errorCode, actions }: ErrorCardProps) => {
  const { t } = useTranslation();

  return (
    <Stack
      align="center"
      gap={32}
      p={40}
      style={{
        width: '100%',
        maxWidth: 440,
        borderRadius: 12,
        border: '1px solid var(--color-border-soft)',
        backgroundColor: 'var(--color-bg-surface)',
        boxShadow: '0px 8px 12px rgba(27, 42, 74, 0.06)'
      }}
    >
      <Stack align="center" gap={12}>
        <Center
          w={64}
          h={64}
          style={{ borderRadius: 999, backgroundColor: 'var(--color-status-danger-soft)' }}
        >
          <IconFigmaAlertTriangle size={32} color="var(--color-status-danger)" />
        </Center>
        <Text
          fz={11}
          fw={700}
          tt="uppercase"
          style={{ color: 'var(--color-status-danger)', letterSpacing: 1 }}
        >
          {badge}
        </Text>
      </Stack>

      <Stack align="center" gap={12} style={{ textAlign: 'center' }}>
        <Title order={2} fz={24} style={{ color: 'var(--color-text-primary)' }}>
          {title}
        </Title>
        <Text fz={14} style={{ color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
          {message}
        </Text>
      </Stack>

      <Box style={{ height: 1, width: '100%', backgroundColor: 'var(--color-border-soft)' }} />

      <Stack gap={12} style={{ width: '100%' }}>
        {actions}
      </Stack>

      {errorCode && (
        <Group gap={8} style={{ opacity: 0.6 }}>
          <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
            {t('errorPage.codeLabel')}
          </Text>
          <Text fz={11} fw={700} style={{ color: 'var(--color-text-muted)' }}>
            {errorCode}
          </Text>
        </Group>
      )}
    </Stack>
  );
};
